import { useState } from "react";
import { Check, Facebook, Link2, Linkedin, Mail, Share2, Twitter } from "lucide-react";
import { toast } from "sonner";

// Social share row — Facebook / X / LinkedIn / Email / copy link, plus the
// device-native share sheet where the browser supports it.
export default function ShareButtons({ title, text = "", url, testId = "share" }) {
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const [copied, setCopied] = useState(false);
  const canNative = typeof navigator !== "undefined" && !!navigator.share;

  const u = encodeURIComponent(shareUrl);
  const summary = encodeURIComponent(text ? `${title} — ${text}` : title);

  const links = [
    { id: "facebook", label: "Share on Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { id: "x", label: "Share on X", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${u}&text=${summary}` },
    { id: "linkedin", label: "Share on LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { id: "email", label: "Share by email", icon: Mail, href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${title}${text ? `\n${text}` : ""}\n\n${shareUrl}`)}` },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    toast.success("Link copied — paste it anywhere.");
    setTimeout(() => setCopied(false), 2000);
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title, text, url: shareUrl });
    } catch {
      // user dismissed the sheet — nothing to do
    }
  };

  const btnCls =
    "flex h-10 w-10 items-center justify-center rounded-full border border-gold/25 text-gold transition-all duration-300 hover:-translate-y-0.5 hover:border-gold hover:bg-gold/10 hover:shadow-[0_8px_20px_-6px_rgba(212,175,55,0.45)]";

  return (
    <div className="flex flex-wrap items-center gap-2.5" data-testid={`${testId}-buttons`}>
      {links.map(({ id, label, icon: Icon, href }) => (
        <a key={id} data-testid={`${testId}-${id}`} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className={btnCls}>
          <Icon className="h-4 w-4" />
        </a>
      ))}
      <button type="button" data-testid={`${testId}-copy`} onClick={copy} aria-label="Copy event link" className={btnCls}>
        {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Link2 className="h-4 w-4" />}
      </button>
      {canNative && (
        <button type="button" data-testid={`${testId}-native`} onClick={nativeShare} aria-label="Share via device" className={btnCls}>
          <Share2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
